// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// 3rd party imports
import { DxBoxComponent, DxFormComponent, DxPopupComponent, DxScrollViewComponent } from 'devextreme-angular';
import { dxElement } from 'devextreme/core/element';

// cc-common imports
import { IconConstants, Utils } from 'cc-common';

// Service imports
import { UserService } from '../../_services/data-services/user.service';

// Model imports
import { DashboardViewMode, DynamicDashboardEntry } from '../_model/dynamic-dashboard-entry';
import { DynamicDashboardItem } from '../_model/dynamic-dashboard-item';
import { DashboardLayout, DynamicDashboardSection } from '../_model/dynamic-dashboard-section';

@Component({
    selector: 'app-dashboard-creator',
    templateUrl: './dashboard-creator.component.html',
    styleUrls: ['./dashboard-creator.component.scss']
})
export class DashboadCreatorComponent implements OnInit {
    @ViewChild('dashboardCreator', { static: false }) dashboardCreator: DxPopupComponent;
    @ViewChild('dashboardForm', { static: false }) dashboardForm: DxFormComponent;
    @ViewChild('dashboardBox', { static: false }) dashboardBox: DxBoxComponent;
    @ViewChild('scrollView', { static: false }) scrollView: DxScrollViewComponent;

    @Input() dashboards: Array<DynamicDashboardEntry>;
    @Input() dashboard: DynamicDashboardEntry;
    @Input() visible = false;
    @Output() onSave: EventEmitter<DynamicDashboardEntry> = new EventEmitter();
    @Output() onCancel: EventEmitter<boolean> = new EventEmitter();

    dashboardLayouts: { text: string; id: string }[];
    dashboardViewModes: string[];
    multiViewIndex = 0;
    boxHeight = -1;
    sectionHeight = -1;
    minSectionHeight = 240;
    sharingAllowed = false;
    systemAllowed = false;

    iconCancel = IconConstants.CANCEL;
    iconSave = IconConstants.SAVE;

    private _timerId: any;

    constructor(private readonly userService: UserService) {
        this.systemAllowed = this.userService.isITUser();
        this.onSystemFlagChanged = this.onSystemFlagChanged.bind(this);
    }

    ngOnInit(): void {
        this.dashboardViewModes = Object.values(DashboardViewMode);
    }

    onShown(e): void {
        this.updateLayoutOptions();
        this.computeSectionHeight();
        this.sharingAllowed =
            !this.dashboard.sharedWorkspace ||
            this.dashboard.sharedWorkspace.createdBy.id === this.userService.getCurrentUserId();
    }

    onHidden(): void {
        this.onCancelClick();
    }

    onSectionCountChange = (e) => {
        const count = e.value;
        const dashboardSections = [];
        for (let i = 0; i < count; i++) {
            const section = this.dashboard.dashboardSections[i] || new DynamicDashboardSection();
            section.resetRatios();
            dashboardSections.push(section);
        }
        this.dashboard.dashboardSections = dashboardSections;
        this.sectionHeight = Math.max(this.boxHeight / count, this.minSectionHeight);
        if (!!this.dashboardCreator && !!this.dashboardCreator.instance) {
            setTimeout(() => this.dashboardCreator.instance.repaint(), 150);
        }
    };

    onViewModeChange = (e) => {
        this.updateLayoutOptions();
        if (!!this.dashboardCreator && !!this.dashboardCreator.instance) {
            setTimeout(() => this.dashboardCreator.instance.repaint(), 150);
        }
    };

    onLayoutChange = (e) => {
        const section: DynamicDashboardSection = e.component.option('elementAttr').section;
        switch (section.layout) {
            case DashboardLayout.TwoByTwo:
                section.dashboardItems = [1, 2, 3, 4].map((id, idx) => {
                    const dashboardItem = section.dashboardItems[idx] || new DynamicDashboardItem();
                    dashboardItem.id = id;
                    dashboardItem.widthRatio = 50;
                    dashboardItem.heightRatio = 50;
                    return dashboardItem;
                });
                break;
            case DashboardLayout.TwoByOne:
                section.dashboardItems = [1, 2].map((id, idx) => {
                    const dashboardItem = section.dashboardItems[idx] || new DynamicDashboardItem();
                    dashboardItem.id = id;
                    dashboardItem.widthRatio = 100;
                    dashboardItem.heightRatio = 50;
                    return dashboardItem;
                });
                break;
            case DashboardLayout.OneByFour:
                section.dashboardItems = [1, 2, 3, 4].map((id, idx) => {
                    const dashboardItem = section.dashboardItems[idx] || new DynamicDashboardItem();
                    dashboardItem.id = id;
                    dashboardItem.widthRatio = 25;
                    dashboardItem.heightRatio = 100;
                    return dashboardItem;
                });
                break;
            case DashboardLayout.OneByThree:
                section.dashboardItems = [1, 2, 3].map((id, idx) => {
                    const dashboardItem = section.dashboardItems[idx] || new DynamicDashboardItem();
                    dashboardItem.id = id;
                    dashboardItem.widthRatio = 33.34;
                    dashboardItem.heightRatio = 100;
                    return dashboardItem;
                });
                break;
            case DashboardLayout.OneByTwo:
                section.dashboardItems = [1, 2].map((id, idx) => {
                    const dashboardItem = section.dashboardItems[idx] || new DynamicDashboardItem();
                    dashboardItem.id = id;
                    dashboardItem.widthRatio = 50;
                    dashboardItem.heightRatio = 100;
                    return dashboardItem;
                });
                break;
            case DashboardLayout.OneByOne:
                section.dashboardItems = [1].map((id, idx) => {
                    const dashboardItem = section.dashboardItems[idx] || new DynamicDashboardItem();
                    dashboardItem.id = id;
                    dashboardItem.widthRatio = 100;
                    dashboardItem.heightRatio = 100;
                    return dashboardItem;
                });
                break;
        }
    };

    onResetLayout = (e) => {
        const section: DynamicDashboardSection = e.component.option('elementAttr').section;
        section.resetRatios();
    };

    uniqueNameValidation = (params: any): Promise<boolean> => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                resolve(this.isUniqueName());
            }, 500);
        });
    };

    validLayoutValidation = (params: any): Promise<boolean> => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                resolve(this.isValidLayout());
            }, 500);
        });
    };

    onCarouselViewNavigationClick(index: number): void {
        this.multiViewIndex = index;
    }

    onSaveClick(): void {
        const result = this.dashboardForm.instance.validate();
        let hasWidgets = true;
        for (let idx = 0; idx < this.dashboard.noOfSections; idx++) {
            const ds = this.dashboard.dashboardSections[idx];
            hasWidgets = ds.validate();
            if (hasWidgets === false) {
                if (this.dashboard.viewMode === DashboardViewMode.Carousel) {
                    this.multiViewIndex = idx;
                } else {
                    const el = document.getElementById(`section-${idx}`);
                    if (!!this.scrollView && !!this.scrollView.instance) {
                        this.scrollView.instance.scrollToElement(el);
                    }
                }
                break;
            }
        }
        if (result.isValid && hasWidgets && this.isUniqueName() && this.isValidLayout()) {
            this.onSave.emit(this.dashboard);
            this.visible = false;
        }
    }

    onCancelClick(): void {
        this.visible = false;
        this.onCancel.emit(true);
    }

    onSystemFlagChanged(): void {
        if (Utils.isFalse(this.dashboard.isSystem)) {
            this.dashboard.allowExternalUsers = false;
        }
    }

    private updateLayoutOptions(): void {
        if (this.dashboard.viewMode === DashboardViewMode.Carousel) {
            this.dashboardLayouts = Object.values(DashboardLayout)
                .filter((val) => [DashboardLayout.OneByThree, DashboardLayout.OneByFour].indexOf(val) === -1)
                .map((val) => {
                    return {
                        text: val,
                        id: val
                    };
                });
        } else if (this.dashboard.viewMode === DashboardViewMode.Scroll) {
            this.dashboardLayouts = Object.values(DashboardLayout)
                .filter((val) => [DashboardLayout.TwoByOne, DashboardLayout.TwoByTwo].indexOf(val) === -1)
                .map((val) => {
                    return {
                        text: val.split(' x ').reverse()[0].trim(),
                        id: val
                    };
                });
        }
    }

    private computeSectionHeight(): void {
        clearTimeout(this._timerId);
        this._timerId = setTimeout(() => {
            if (!!this.dashboardBox && !!this.dashboardBox.instance) {
                // tslint:disable-next-line:deprecation
                const el: dxElement = this.dashboardBox.instance.element();
                this.boxHeight = el.clientHeight - 80;
                this.sectionHeight = Math.max(this.boxHeight / this.dashboard.noOfSections, this.minSectionHeight);
            }
        }, 250);
    }

    private isUniqueName(): boolean {
        return (
            this.dashboards
                .filter((d) => d.id !== this.dashboard.id)
                .map((d) => d.name.trim())
                .indexOf((this.dashboard.name || '').trim()) === -1
        );
    }

    private isValidLayout(): boolean {
        let validLayout = true;
        this.dashboard.dashboardSections.forEach((ds) => {
            validLayout = validLayout && this.dashboardLayouts.map((dl) => dl.id).indexOf(ds.layout) !== -1;
        });
        return validLayout;
    }
}
